import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CheckCircle2, MapPin, Navigation, Trash2, Mail, Printer, Sparkles, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import InvoicePrintHeader from "@/components/InvoicePrintHeader";
import DeliveryPhotos from "@/components/DeliveryPhotos";
import CardknoxPayDialog from "@/components/CardknoxPayDialog";
import { logError } from "@/lib/log";

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [chargingSaved, setChargingSaved] = useState(false);
  const [customer, setCustomer] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/invoices/${id}`);
    setInv(r.data);
    if (r.data?.customer_id) {
      try {
        const cr = await api.get(`/customers/${r.data.customer_id}`);
        setCustomer(cr.data);
      } catch (e) { logError("Invoice customer load failed (saved-card UI falls through):", e); }
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const chargeSavedCard = async () => {
    if (!window.confirm(`Charge ${customer?.name || "customer"} ${fmtMoney(inv.total)} to card ending in ${customer?.card_on_file?.last4 || "????"}?`)) return;
    setChargingSaved(true);
    try {
      const { data } = await api.post(`/payments/cardknox/charge-saved`, { invoice_id: id });
      toast.success(`Charged ${fmtMoney(inv.total)} · Ref ${data.ref_num || ""}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setChargingSaved(false); }
  };

  const markPaid = async () => {
    try {
      await api.post(`/invoices/${id}/mark-paid`, { method: "manual", note: "" });
      toast.success("Marked as paid");
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const resendEmail = async () => {
    try {
      await api.post(`/invoices/${id}/resend-email`);
      toast.success("Invoice email re-sent to customer");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  const runAIEstimate = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/invoices/${id}/ai-estimate`);
      const n = (r.data?.estimates || []).length;
      toast.success(n > 0 ? `AI analyzed ${n} pool${n === 1 ? "" : "s"}` : "AI couldn't read the photos confidently");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setAiBusy(false); }
  };

  const removeInvoice = async () => {
    if (!window.confirm("Delete this invoice? The original ticket will be reopened.")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      navigate("/admin/invoices");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
  };

  if (!inv) return <div className="overline">Loading…</div>;

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl">
      <Link to="/admin/invoices" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground no-print" data-testid="back-to-invoices">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to invoices
      </Link>

      <div className="relative border border-border p-8 sm:p-12 print-container shadow-md overflow-hidden" style={{ borderRadius: 6, background: "linear-gradient(180deg, #DCEEFF 0%, #EFF6FF 35%, #F8FBFF 75%, #FFFFFF 100%)" }}>
        {/* Subtle watermark stamp for paid invoices */}
        {inv.status === "paid" && (
          <div
            aria-hidden
            className="pointer-events-none absolute select-none"
            style={{
              top: "38%", right: "8%", transform: "rotate(-14deg)",
              color: "rgba(22, 163, 74, 0.10)",
              fontFamily: "'IBM Plex Serif',serif",
              fontWeight: 900,
              fontSize: 120,
              letterSpacing: "0.08em",
              zIndex: 0,
            }}
          >PAID</div>
        )}
        <div className="relative" style={{ zIndex: 1 }}>
          <InvoicePrintHeader inv={inv} />

          {/* Bill To / Ship To / Invoice meta — Zylker-style 3-column block */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10">
            <div>
              <p className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-bold mb-2">Bill To</p>
              <div className="font-semibold text-base leading-tight" style={{ fontFamily: "'IBM Plex Serif',serif", color: "#60A5FA" }}>
                {inv.customer_name}
              </div>
              {inv.customer_address && (
                <div className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  {inv.customer_address.split(",").map((line, i) => (<div key={`addr-${i}-${line.trim()}`}>{line.trim()}</div>))}
                </div>
              )}
              {inv.customer_email && <div className="text-sm text-muted-foreground mt-1">{inv.customer_email}</div>}
            </div>
            {inv.pool_name && (
              <div>
                <p className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-bold mb-2">Ship To</p>
                <div className="font-semibold text-base leading-tight" style={{ fontFamily: "'IBM Plex Serif',serif", color: "#60A5FA" }}>
                  {inv.pool_name}
                </div>
                <div className="text-sm text-muted-foreground mt-1 leading-relaxed flex items-start gap-1">
                  <MapPin className="w-3 h-3 mt-1 flex-shrink-0" />
                  <span>{inv.pool_address}</span>
                </div>
                {inv.pool_address && (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(inv.pool_address)}`} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1 no-print" data-testid="invoice-maps-link">
                    <Navigation className="w-3 h-3" /> Open in Google Maps
                  </a>
                )}
              </div>
            )}
            <div className={inv.pool_name ? "" : "sm:col-start-3"}>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Invoice Date:</span>
                  <span className="font-mono">{fmtDate(inv.issued_at)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Delivery Date:</span>
                  <span className="font-mono">{fmtDate(inv.delivery_date)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-muted-foreground">Terms:</span>
                  <span className={inv.status === "paid" ? "text-success font-bold uppercase tracking-wider text-xs" : "font-medium"}>
                    {inv.status === "paid" ? "PAID" : "Due on Receipt"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Line items — bold primary banner header */}
          <div className="mb-2">
            <div className="grid grid-cols-12 text-white text-[11px] tracking-[0.18em] uppercase font-bold py-3 px-4" style={{ background: "#60A5FA" }}>
              <div className="col-span-1">#</div>
              <div className="col-span-5">Item &amp; Description</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Rate</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>
            {(inv.line_items && inv.line_items.length > 0 ? inv.line_items : [{
              description: "Bulk chlorine delivery",
              quantity: inv.gallons,
              unit_price: inv.price_per_gallon,
              amount: inv.subtotal,
            }]).map((li, i) => (
              <div key={`li-${i}-${li.description}`} className="grid grid-cols-12 py-4 px-4 border-b border-border text-sm items-start">
                <div className="col-span-1 text-muted-foreground mono-num">{i + 1}</div>
                <div className="col-span-5 font-medium">
                  {li.description}
                  {li.taxable === false && <div className="text-xs text-muted-foreground mt-0.5 font-normal">(non-taxable)</div>}
                </div>
                <div className="col-span-2 text-right mono-num">{li.quantity}</div>
                <div className="col-span-2 text-right mono-num text-muted-foreground">{fmtMoney(li.unit_price)}</div>
                <div className="col-span-2 text-right mono-num font-semibold">{fmtMoney(li.amount)}</div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="flex justify-end mt-8">
            <div className="w-full sm:w-96 space-y-2">
              <div className="flex justify-between text-sm py-1 px-1">
                <span className="text-muted-foreground">Sub Total</span>
                <span className="mono-num">{fmtMoney(inv.subtotal)}</span>
              </div>
              {inv.tax_exempt ? (
                <div className="flex justify-between text-sm py-1 px-1">
                  <span className="text-muted-foreground flex items-center gap-2">
                    Tax
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-success/10 text-success text-[10px] font-bold uppercase tracking-wider" data-testid="invoice-tax-exempt-badge">
                      Exempt{inv.tax_exempt_number ? ` · ${inv.tax_exempt_number}` : ""}
                    </span>
                  </span>
                  <span className="mono-num">{fmtMoney(0)}</span>
                </div>
              ) : (
                <div className="flex justify-between text-sm py-1 px-1">
                  <span className="text-muted-foreground">Tax ({inv.tax_rate}%)</span>
                  <span className="mono-num">{fmtMoney(inv.tax)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm py-2 px-1 border-t border-border font-semibold">
                <span>Total</span>
                <span className="mono-num">{fmtMoney(inv.total)}</span>
              </div>
              {/* Bold balance-due banner — the showstopper */}
              <div className="text-white px-5 py-3 flex justify-between items-baseline mt-1" style={{ background: "#60A5FA" }}>
                <span className="font-bold tracking-[0.14em] uppercase text-xs">Balance Due</span>
                <span className="font-display text-2xl font-black tracking-tighter mono-num">{fmtMoney(inv.status === "paid" ? 0 : inv.total)}</span>
              </div>
            </div>
          </div>

          {/* Print-only footer with thank-you + payment instructions */}
          <div className="hidden print:block mt-12 pt-6 border-t border-border">
            <p className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground font-bold mb-2">Notes</p>
            <p className="text-sm text-foreground" style={{ fontFamily: "'IBM Plex Serif',serif" }}>
              Thank you for your business.
            </p>
            <p className="text-[10px] text-muted-foreground mt-4 leading-relaxed">
              Questions? Reply to this invoice or call us — we&apos;re happy to help.
            </p>
          </div>

        {inv.status === "unpaid" && (
          <div className="mt-8 pt-6 border-t border-border flex flex-col items-end gap-3 no-print">
            <div className="flex items-center gap-3 flex-wrap justify-end">
              <Button onClick={() => window.print()} variant="outline" className="rounded-sm h-12 px-6" data-testid="print-invoice-button">
                <Printer className="w-4 h-4 mr-2" /> Print / PDF
              </Button>
              <Button onClick={resendEmail} variant="outline" className="rounded-sm h-12 px-6" data-testid="resend-email-button">
                <Mail className="w-4 h-4 mr-2" /> Resend email
              </Button>
              <Button onClick={markPaid} variant="outline" className="rounded-sm h-12 px-6" data-testid="mark-paid-button">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Mark as paid (manual)
              </Button>
              {customer?.card_on_file?.token ? (
                <Button
                  onClick={chargeSavedCard}
                  disabled={chargingSaved}
                  className="rounded-sm h-12 px-6 font-semibold"
                  data-testid="charge-saved-card-button"
                >
                  {chargingSaved ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Charging…</>) : (<><CreditCard className="w-4 h-4 mr-2" /> Charge card ending {customer.card_on_file.last4}</>)}
                </Button>
              ) : (
                <Button
                  onClick={() => setPayOpen(true)}
                  className="rounded-sm h-12 px-6 font-semibold"
                  data-testid="charge-new-card-button"
                >
                  <CreditCard className="w-4 h-4 mr-2" /> Charge card
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Powered by Cardknox · {customer?.card_on_file?.token ? "Customer has a card on file." : "Enter card details to charge — optionally save for future invoices."}
            </p>
          </div>
        )}

        <CardknoxPayDialog
          open={payOpen}
          onClose={() => setPayOpen(false)}
          invoice={inv}
          onPaid={() => load()}
        />

        {inv.status === "paid" && (
          <div className="mt-8 pt-6 border-t border-border flex justify-end no-print">
            <Button onClick={() => window.print()} variant="outline" className="rounded-sm h-10 px-5" data-testid="print-invoice-button">
              <Printer className="w-4 h-4 mr-2" /> Print / PDF
            </Button>
          </div>
        )}

        <AIEstimatePanel invoice={inv} busy={aiBusy} onRun={runAIEstimate} />

        <DeliveryPhotos photos={inv.delivery_photos} extraPhotos={inv.delivery_photo_data_urls} />

        <div className="mt-8 pt-6 border-t border-border flex justify-end no-print">
          <Button onClick={removeInvoice} variant="ghost" className="rounded-sm text-destructive hover:text-destructive" data-testid="delete-invoice-button">
            <Trash2 className="w-4 h-4 mr-2" /> Delete invoice
          </Button>
        </div>
        </div>
      </div>
    </div>
  );
}

function AIEstimatePanel({ invoice, busy, onRun }) {
  const hasPhotos = (invoice.delivery_photos || []).some(
    (p) => (p.photo_before_data_url || "").trim() && (p.photo_after_data_url || "").trim()
  );
  if (!hasPhotos) return null;
  const estimates = invoice.ai_chlorine_estimates || [];
  const totalEstimated = estimates.reduce((s, e) => s + Number(e.gallons_delivered || 0), 0);
  const actualGallons = Number(invoice.gallons || 0);
  const variance = totalEstimated > 0 ? Math.abs(totalEstimated - actualGallons) : 0;
  const variancePct = totalEstimated > 0 ? (variance / Math.max(actualGallons, 1)) * 100 : 0;
  const tooHigh = variancePct > 25;

  return (
    <section className="mt-8 border border-primary/30 bg-primary/5 rounded-sm no-print" data-testid="ai-estimate-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-primary/20">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-display text-base font-bold tracking-tight">AI chlorine-level estimate</h3>
        </div>
        <Button
          onClick={onRun}
          disabled={busy}
          size="sm"
          variant="outline"
          className="rounded-sm h-9"
          data-testid="ai-estimate-run-button"
        >
          {busy ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Analyzing…</> : <><Sparkles className="w-3.5 h-3.5 mr-2" /> {estimates.length > 0 ? "Re-run estimate" : "Run AI estimate"}</>}
        </Button>
      </div>

      {estimates.length === 0 && !busy && (
        <p className="px-5 py-4 text-xs text-muted-foreground font-mono" data-testid="ai-estimate-empty">
          Tap "Run AI estimate" to have Gemini analyze the before/after photos and estimate how many gallons were actually delivered per pool.
        </p>
      )}

      {estimates.length > 0 && (
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Driver entered</div>
              <div className="mono-num font-bold text-lg" data-testid="ai-actual-gallons">{actualGallons.toFixed(1)} gal</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">AI estimated</div>
              <div className="mono-num font-bold text-lg" data-testid="ai-estimated-gallons">{totalEstimated.toFixed(1)} gal</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Variance</div>
              <div className={`mono-num font-bold text-lg ${tooHigh ? "text-destructive" : "text-success"}`} data-testid="ai-variance">
                {variance.toFixed(1)} gal ({variancePct.toFixed(0)}%)
              </div>
            </div>
          </div>

          {tooHigh && (
            <div className="border border-destructive/40 bg-destructive/5 rounded-sm px-3 py-2 text-xs text-destructive font-mono" data-testid="ai-variance-warning">
              AI estimate differs from driver entry by more than 25%. Review the photos and the entered gallons.
            </div>
          )}

          <div className="space-y-2">
            {estimates.map((e) => (
              <div key={e.pool_id || e.pool_name} className="border border-primary/20 bg-background rounded-sm px-3 py-2 text-xs" data-testid={`ai-estimate-row-${e.pool_id || ""}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-bold">{e.pool_name || "Pool"} <span className="text-muted-foreground font-normal">({e.tank_size || "?"}-gal tank)</span></div>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{e.confidence_pct}% confidence</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mono-num">
                  <div><span className="text-muted-foreground text-[10px] uppercase tracking-wider">Before</span><div className="font-semibold">{Number(e.gallons_before).toFixed(1)} gal</div></div>
                  <div><span className="text-muted-foreground text-[10px] uppercase tracking-wider">After</span><div className="font-semibold">{Number(e.gallons_after).toFixed(1)} gal</div></div>
                  <div><span className="text-muted-foreground text-[10px] uppercase tracking-wider">Delivered</span><div className="font-bold text-primary">{Number(e.gallons_delivered).toFixed(1)} gal</div></div>
                </div>
                {e.notes && <div className="mt-1 text-[11px] text-muted-foreground italic">{e.notes}</div>}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground font-mono pt-1">Estimate by Gemini 3 Pro vision. Photos: before & after each pool. This is an aid — driver's entered gallons remain the source of truth.</p>
        </div>
      )}
    </section>
  );
}
